"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";

import { Link } from "@/navigation";

const NAMESPACE = "new-fdm-fused-deposition-modeling";

const PROTOTYPING_ITEMS = [
	{ n: 1, hasDesc: true },
	{ n: 2, hasDesc: true },
	{ n: 3, hasDesc: true },
	{ n: 4, hasDesc: true },
	{ n: 5, hasDesc: true },
] as const;

const INDUSTRIAL_ITEMS = [
	{ n: 6, hasDesc: true },
	{ n: 7, hasDesc: true },
	{ n: 8, hasDesc: true },
	{ n: 9, hasDesc: true },
] as const;

function AccordionItem({
	n,
	hasDesc,
	isOpen,
	onToggle,
	t,
}: {
	n: number;
	hasDesc: boolean;
	isOpen: boolean;
	onToggle: () => void;
	t: ReturnType<typeof useTranslations>;
}) {
	const title = t(`fdmMaterialOptionsItem${n}Title` as any);
	const subtitle = t(`fdmMaterialOptionsItem${n}Subtitle` as any);

	return (
		<div>
			<button onClick={onToggle} className="flex w-full items-center justify-between py-4 text-left">
				<div className="flex items-baseline gap-2">
					<span className="text-base font-bold text-black sm:text-lg">{title}</span>
					{subtitle && <span className="text-base font-bold text-black sm:text-lg">({subtitle})</span>}
				</div>
				<svg
					className={`h-5 w-5 shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
					viewBox="0 0 24 24"
					fill="none"
					stroke="#6B9B37"
					strokeWidth="3"
					strokeLinecap="round"
					strokeLinejoin="round"
				>
					<polyline points="6 9 12 15 18 9" />
				</svg>
			</button>

			{isOpen && hasDesc && (
				<div className="pb-6 pl-0 pr-8">
					<p className="text-sm leading-relaxed text-black md:text-base">
						{t(`fdmMaterialOptionsItem${n}Desc` as any)}
					</p>
				</div>
			)}
		</div>
	);
}

export default function FdmMaterialOptions() {
	const t = useTranslations(NAMESPACE);
	const [leftOpen, setLeftOpen] = useState(0);
	const [rightOpen, setRightOpen] = useState(0);

	const toggleLeft = (index: number) => {
		setLeftOpen(leftOpen === index ? -1 : index);
	};

	const toggleRight = (index: number) => {
		setRightOpen(rightOpen === index ? -1 : index);
	};

	return (
		<section className="bg-[#f6f6f6] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
					{t("fdmMaterialOptionsTitle")}
				</h2>
				<p className="mb-6 text-sm leading-relaxed text-black sm:mb-8 md:mb-10 md:text-base lg:mb-12">
					{t("fdmMaterialOptionsDesc")}
				</p>

				<div className="grid gap-8 lg:grid-cols-2 lg:gap-12">
					{/* Prototyping Materials */}
					<div>
						<h3 className="mb-4 text-lg font-bold text-black sm:text-xl md:text-2xl">
							{t("fdmMaterialOptionsPrototypingTitle")}
						</h3>
						<div className="divide-y divide-gray-200 border-t border-gray-200">
							{PROTOTYPING_ITEMS.map((item, idx) => (
								<AccordionItem
									key={item.n}
									n={item.n}
									hasDesc={item.hasDesc}
									isOpen={leftOpen === idx}
									onToggle={() => toggleLeft(idx)}
									t={t}
								/>
							))}
						</div>
					</div>

					{/* Industrial Materials */}
					<div>
						<h3 className="mb-4 text-lg font-bold text-black sm:text-xl md:text-2xl">
							{t("fdmMaterialOptionsIndustrialTitle")}
						</h3>
						<div className="divide-y divide-gray-200 border-t border-gray-200">
							{INDUSTRIAL_ITEMS.map((item, idx) => (
								<AccordionItem
									key={item.n}
									n={item.n}
									hasDesc={item.hasDesc}
									isOpen={rightOpen === idx}
									onToggle={() => toggleRight(idx)}
									t={t}
								/>
							))}
						</div>
					</div>
				</div>

				<div className="mt-8">
					<Link
						href="/contact-us"
						className="inline-flex items-center justify-center rounded bg-themePrimary400 px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:bg-opacity-80 hover:text-white md:px-8 md:py-3.5 md:text-base"
					>
						{t("fdmMaterialOptionsContactUs")}
					</Link>
				</div>
			</div>
		</section>
	);
}
